import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Select from '../../../components/ui/Select';

const CardTransactionHistory = ({ transactions, paymentSources }) => {
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterPaymentSource, setFilterPaymentSource] = useState('all');

  const categoryOptions = [
    { value: 'all', label: 'All Categories' },
    { value: 'Shopping', label: 'Shopping' },
    { value: 'Food & Dining', label: 'Food & Dining' },
    { value: 'Transportation', label: 'Transportation' },
    { value: 'Entertainment', label: 'Entertainment' }
  ];

  const paymentSourceOptions = [
    { value: 'all', label: 'All Sources' },
    ...paymentSources?.map(source => ({
      value: source?.symbol,
      label: source?.name
    }))
  ];

  const getPaymentSourceColor = (symbol) => {
    const source = paymentSources?.find(s => s?.symbol === symbol);
    return source?.color || '#6B7280';
  };

  const getPaymentSourceIcon = (symbol) => {
    const source = paymentSources?.find(s => s?.symbol === symbol);
    return source?.icon || 'CreditCard';
  };

  const filteredTransactions = transactions?.filter(tx => {
    const categoryMatch = filterCategory === 'all' || tx?.category === filterCategory;
    const sourceMatch = filterPaymentSource === 'all' || tx?.paymentSource === filterPaymentSource;
    return categoryMatch && sourceMatch;
  });

  return (
    <div className="card">
      <div className="card-header">
        <div>
          <h2 className="card-title">Transaction History</h2>
          <p className="card-description">Recent card purchases and payments</p>
        </div>
      </div>
      <div className="card-content">
        {/* Filters */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <Select
            options={categoryOptions}
            value={filterCategory}
            onChange={setFilterCategory}
            placeholder="Filter by category"
          />
          <Select
            options={paymentSourceOptions}
            value={filterPaymentSource}
            onChange={setFilterPaymentSource}
            placeholder="Filter by payment source"
          />
        </div>

        {/* Transaction List */}
        <div className="space-y-3">
          {filteredTransactions?.length === 0 ? (
            <div className="text-center py-12">
              <Icon name="Receipt" size={48} className="text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">No transactions found</p>
            </div>
          ) : (
            filteredTransactions?.map((transaction) => (
              <div 
                key={transaction?.id}
                className="p-4 bg-muted/30 hover:bg-muted/50 rounded-lg transition-colors cursor-pointer"
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3 flex-1">
                    {/* Merchant Icon */}
                    <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                      <Icon name={transaction?.icon} size={20} className="text-primary" />
                    </div>

                    {/* Transaction Details */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h4 className="text-sm font-semibold text-foreground">{transaction?.merchant}</h4>
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                          transaction?.status === 'completed' 
                            ? 'bg-success/10 text-success' :'bg-warning/10 text-warning'
                        }`}>
                          {transaction?.status}
                        </span>
                      </div>
                      <p className="text-xs text-muted-foreground mb-2">{transaction?.category}</p>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-muted-foreground">
                          {transaction?.date} at {transaction?.time}
                        </span>
                        <span className="text-xs text-muted-foreground">•</span>
                        <div className="flex items-center gap-1">
                          <Icon 
                            name={getPaymentSourceIcon(transaction?.paymentSource)} 
                            size={12} 
                            style={{ color: getPaymentSourceColor(transaction?.paymentSource) }}
                          />
                          <span className="text-xs font-medium" style={{ color: getPaymentSourceColor(transaction?.paymentSource) }}>
                            {transaction?.paymentSource}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Amount */}
                  <div className="text-right flex-shrink-0 ml-4">
                    <p className="text-lg font-bold text-foreground">
                      ${Math.abs(transaction?.amount)?.toFixed(2)}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {transaction?.cryptoAmount?.toLocaleString('en-US', { maximumFractionDigits: 4 })} {transaction?.paymentSource}
                    </p>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Summary */}
        {filteredTransactions?.length > 0 && (
          <div className="mt-6 pt-6 border-t border-border">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Total Transactions</span>
              <span className="text-sm font-semibold text-foreground">{filteredTransactions?.length}</span>
            </div>
            <div className="flex items-center justify-between mt-2">
              <span className="text-sm text-muted-foreground">Total Spent</span>
              <span className="text-lg font-bold text-foreground">
                ${filteredTransactions?.reduce((sum, tx) => sum + Math.abs(tx?.amount), 0)?.toFixed(2)}
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CardTransactionHistory;